import KillProcessIcon12px from '@jetbrains/icons/kill-process-12px.svg?react'
import KillProcessIcon from '@jetbrains/icons/kill-process.svg?react'
import StopIcon12px from '@jetbrains/icons/stop-12px.svg?react'
import StopIcon from '@jetbrains/icons/stop.svg?react'
import Button from '@jetbrains/ring-ui-built/components/button/button'
import {ControlsHeight} from '@jetbrains/ring-ui-built/components/global/controls-height'
import {IconButton} from '@jetbrains/teamcity-ui/dist/components'
import classNames from 'classnames'
import {useRef} from 'react'

import {useControlsHeight} from '../../../hooks/useControlsHeight'
import {useUserDisplayName} from '../../../hooks/useUserDisplayName'
import type {BuildId} from '../../../types'
import {KillOperationKind} from '../../../types'
import {BS} from '../../../types/BS_types'
import {useSequenceLoaderReadyHandler} from '../SequenceLoader/SequenceLoader.hooks'

import {useIsCancellable} from './StopBuild.hooks'
import {StopButtonType} from './StopBuild.types'
import type {Props} from './StopBuild.types'

import styles from './StopBuild.module.css'

const noop = () => Promise.resolve(false)

/**
 * BS.StopBuildDialog.showStopBuildDialog([21740961], '', 2, null)
 * Shows the dialog to one of the following 3 actions: remove a build from the queue, stop a running build, remove a finished build.
 *
 * @param promoIds        array of promotion identifiers. Mostly one identifier only.
 * @param defaultMessage
 * @param operationKind      See StopBuildAction.KillOperationKind:
 *                           1 - remove a queued build,
 *                           2 - stop a running build,
 *                           3 - remove a finished build.
 * @param reStopMessage
 */
type ShowDialogFn = (
  arg0: [BuildId],
  arg1: string,
  arg2: KillOperationKind,
  arg3: HTMLSpanElement | null | undefined,
) => Promise<boolean>
export default function StopBuild({
  state = 'running',
  actualStopMessage = '',
  buttonType = StopButtonType.ICON_BUTTON,
  onStop = noop,
  updateResults = noop,
  className,
  isStopping,
  isInterrupted,
  stoppedWithComment,
  stoppedBy,
  buttonText,
  primary,
  agentName,
  buildId,
  withBorder,
}: Props) {
  const isCancellable = useIsCancellable(buildId)
  const stoppedByDisplayName = useUserDisplayName(stoppedBy)
  const commentRef = useRef<HTMLSpanElement>(null)

  useSequenceLoaderReadyHandler(true)

  const getTitle: () => string = (): string => {
    const agentMention = agentName != null ? ` on ${agentName}` : ''

    if (state === 'finished') {
      return ''
    }

    if (!isCancellable) {
      return isInterrupted ? 'Build is stopping' : ''
    }

    if (state === 'queued') {
      return 'Cancel build...'
    }

    if (isStopping) {
      return 'Build is stopping'
    }

    if (isInterrupted) {
      return `Build${agentMention} is stopping. Click to force stop`
    }

    return `Stop build${agentMention}...`
  }

  const getButtonText: () => string = () => (state === 'running' ? 'Stop...' : 'Cancel...')

  const getOperationKind: () => KillOperationKind = () =>
    state === 'running' ? KillOperationKind.STOP : KillOperationKind.CANCEL

  const getShowHandler: () => ShowDialogFn = () => {
    if (BS?.StopBuildDialog) {
      return BS.StopBuildDialog.showStopBuildDialog.bind(BS.StopBuildDialog)
    } else {
      return noop
    }
  }

  const handleShowStopDialog: () => Promise<void> = async () => {
    const showDialog: ShowDialogFn = getShowHandler()
    const actionTriggered = await showDialog(
      [buildId],
      actualStopMessage,
      getOperationKind(),
      commentRef.current,
    )

    if (actionTriggered) {
      onStop(buildId)
      updateResults()
    }
  }

  const classes = classNames(styles.stopBuild, className, {
    [styles.invisible]: state === 'finished' || (!isCancellable && !isInterrupted),
  })
  const controlsHeight = useControlsHeight()
  return (
    <>
      {stoppedWithComment != null && (
        <span hidden ref={commentRef}>
          {'This build has already been stopped'}
          {stoppedByDisplayName != null && (
            <>
              {' by '}
              <em>{stoppedByDisplayName}</em>
            </>
          )}
          {' with message '}
          <em>{stoppedWithComment}</em>
        </span>
      )}
      {buttonType === StopButtonType.ICON_BUTTON ? (
        <IconButton
          height={controlsHeight}
          disabled={!isCancellable}
          className={classes}
          title={getTitle()}
          onClick={handleShowStopDialog}
          loader={isStopping || (!isCancellable && isInterrupted)}
          icon={
            withBorder && controlsHeight === ControlsHeight.S
              ? isInterrupted
                ? KillProcessIcon12px
                : StopIcon12px
              : isInterrupted
                ? KillProcessIcon
                : StopIcon
          }
          iconClassName={classNames({[styles.iconOnly]: !buttonText})}
          danger={buttonText ? isInterrupted && isCancellable : true}
          primary={primary}
          withBorder={withBorder}
        >
          {buttonText}
        </IconButton>
      ) : (
        <Button
          disabled={!isCancellable}
          className={classes}
          title={getTitle()}
          onClick={handleShowStopDialog}
          loader={isStopping || (!isCancellable && isInterrupted)}
          danger={isInterrupted && isCancellable}
        >
          {getButtonText()}
        </Button>
      )}
    </>
  )
}
